import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Save, Headphones } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function PodcastManager() {
  const [podcasts, setPodcasts] = useState([]);
  const [episodes, setEpisodes] = useState({});
  const [podForm, setPodForm] = useState({ title: "", slug: "", description: "", cover_image: "", host_name: "" });
  const [epForm, setEpForm] = useState({ podcast_id: "", title: "", description: "", audio_url: "", duration: "", episode_number: 1 });
  const [editPodId, setEditPodId] = useState(null);
  const [editEpId, setEditEpId] = useState(null);
  const [podOpen, setPodOpen] = useState(false);
  const [epOpen, setEpOpen] = useState(false);

  const load = async () => {
    const r = await api.get("/podcasts").catch(() => ({ data: [] }));
    setPodcasts(r.data);
    for (const p of r.data) {
      const er = await api.get(`/episodes?podcast_id=${p.id}`).catch(() => ({ data: [] }));
      setEpisodes(prev => ({ ...prev, [p.id]: er.data }));
    }
  };
  useEffect(() => { load(); }, []);

  const savePodcast = async () => {
    if (!podForm.title) { toast.error("Title required"); return; }
    try {
      const data = { ...podForm, slug: podForm.slug || podForm.title.toLowerCase().replace(/\s+/g, "-") };
      if (editPodId) await api.put(`/podcasts/${editPodId}`, data);
      else await api.post("/podcasts", data);
      toast.success("Saved"); setPodOpen(false); load();
    } catch { toast.error("Failed"); }
  };

  const saveEpisode = async () => {
    if (!epForm.title || !epForm.podcast_id) { toast.error("Title and podcast required"); return; }
    try {
      if (editEpId) await api.put(`/episodes/${editEpId}`, epForm);
      else await api.post("/episodes", epForm);
      toast.success("Saved"); setEpOpen(false); load();
    } catch { toast.error("Failed"); }
  };

  const deletePodcast = async (id) => { if (window.confirm("Delete podcast and all episodes?")) { try { await api.delete(`/podcasts/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); } } };
  const deleteEpisode = async (id) => { if (window.confirm("Delete episode?")) { try { await api.delete(`/episodes/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); } } };

  return (
    <div data-testid="podcast-manager">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-serif font-bold text-foreground">Podcasts</h1>
        <div className="flex gap-2">
          <Dialog open={epOpen} onOpenChange={setEpOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="font-sans" onClick={() => { setEpForm({ podcast_id: podcasts[0]?.id || "", title: "", description: "", audio_url: "", duration: "", episode_number: 1 }); setEditEpId(null); setEpOpen(true); }} data-testid="new-episode-btn">
                <Plus className="h-4 w-4 mr-2" /> Episode
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-serif">{editEpId ? "Edit" : "New"} Episode</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-4">
                <div><Label className="font-sans text-sm">Podcast *</Label>
                  <select className="w-full mt-1 border border-slate-200 rounded px-3 py-2 text-sm" value={epForm.podcast_id} onChange={e => setEpForm(p => ({ ...p, podcast_id: e.target.value }))} data-testid="episode-podcast-select">
                    {podcasts.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                  </select>
                </div>
                <div><Label className="font-sans text-sm">Title *</Label><Input value={epForm.title} onChange={e => setEpForm(p => ({ ...p, title: e.target.value }))} className="mt-1" data-testid="episode-title" /></div>
                <div><Label className="font-sans text-sm">Description</Label><Textarea value={epForm.description} onChange={e => setEpForm(p => ({ ...p, description: e.target.value }))} rows={2} className="mt-1" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="font-sans text-sm">Audio URL</Label><Input value={epForm.audio_url} onChange={e => setEpForm(p => ({ ...p, audio_url: e.target.value }))} className="mt-1" data-testid="episode-audio" /></div>
                  <div><Label className="font-sans text-sm">Duration</Label><Input value={epForm.duration} onChange={e => setEpForm(p => ({ ...p, duration: e.target.value }))} placeholder="25:30" className="mt-1" /></div>
                </div>
                <div><Label className="font-sans text-sm">Episode #</Label><Input type="number" value={epForm.episode_number} onChange={e => setEpForm(p => ({ ...p, episode_number: parseInt(e.target.value) || 1 }))} className="mt-1" /></div>
                <Button onClick={saveEpisode} className="w-full bg-[#1E88E5] hover:bg-[#1565C0] font-sans" data-testid="save-episode"><Save className="h-4 w-4 mr-2" />Save</Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={podOpen} onOpenChange={setPodOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#1E88E5] hover:bg-[#1565C0] font-sans" onClick={() => { setPodForm({ title: "", slug: "", description: "", cover_image: "", host_name: "" }); setEditPodId(null); setPodOpen(true); }} data-testid="new-podcast-btn">
                <Plus className="h-4 w-4 mr-2" /> Podcast
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-serif">{editPodId ? "Edit" : "New"} Podcast</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-4">
                <div><Label className="font-sans text-sm">Title *</Label><Input value={podForm.title} onChange={e => setPodForm(p => ({ ...p, title: e.target.value }))} className="mt-1" data-testid="podcast-title" /></div>
                <div><Label className="font-sans text-sm">Host</Label><Input value={podForm.host_name} onChange={e => setPodForm(p => ({ ...p, host_name: e.target.value }))} className="mt-1" data-testid="podcast-host" /></div>
                <div><Label className="font-sans text-sm">Cover Image URL</Label><Input value={podForm.cover_image} onChange={e => setPodForm(p => ({ ...p, cover_image: e.target.value }))} className="mt-1" /></div>
                <div><Label className="font-sans text-sm">Description</Label><Textarea value={podForm.description} onChange={e => setPodForm(p => ({ ...p, description: e.target.value }))} rows={3} className="mt-1" /></div>
                <Button onClick={savePodcast} className="w-full bg-[#1E88E5] hover:bg-[#1565C0] font-sans" data-testid="save-podcast"><Save className="h-4 w-4 mr-2" />Save</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="space-y-6">
        {podcasts.map(pod => (
          <div key={pod.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700" data-testid={`admin-podcast-${pod.id}`}>
            <div className="p-6 flex items-start gap-4">
              {pod.cover_image && <img src={pod.cover_image} alt={pod.title} className="w-20 h-20 object-cover shrink-0" />}
              <div className="flex-1 min-w-0">
                <h3 className="font-serif font-bold text-lg text-foreground flex items-center gap-2"><Headphones className="h-4 w-4 text-[#1E88E5]" />{pod.title}</h3>
                <p className="text-sm font-sans text-muted-foreground">{pod.host_name} &middot; {(episodes[pod.id] || []).length} episodes</p>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => { setPodForm({ title: pod.title, slug: pod.slug, description: pod.description || "", cover_image: pod.cover_image || "", host_name: pod.host_name || "" }); setEditPodId(pod.id); setPodOpen(true); }}><Pencil className="h-3 w-3" /></Button>
                <Button variant="ghost" size="sm" className="text-red-600" onClick={() => deletePodcast(pod.id)}><Trash2 className="h-3 w-3" /></Button>
              </div>
            </div>
            {(episodes[pod.id] || []).length > 0 && (
              <div className="border-t border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700">
                {(episodes[pod.id] || []).map(ep => (
                  <div key={ep.id} className="px-6 py-3 flex items-center justify-between">
                    <div>
                      <span className="text-xs text-muted-foreground font-sans">Ep {ep.episode_number}</span>
                      <p className="text-sm font-sans font-medium text-foreground">{ep.title}</p>
                      <span className="text-xs text-muted-foreground font-sans">{ep.duration}</span>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEpForm({ podcast_id: ep.podcast_id, title: ep.title, description: ep.description || "", audio_url: ep.audio_url || "", duration: ep.duration || "", episode_number: ep.episode_number || 1 }); setEditEpId(ep.id); setEpOpen(true); }}><Pencil className="h-3 w-3" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => deleteEpisode(ep.id)}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
